import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { supabase } from '../services/supabase'
import { useOrganization } from '../contexts/OrganizationContext'
import Card from '../components/Card'
import Button from '../components/Button'
import {
  User,
  Mail,
  Shield,
  Key,
  CheckCircle,
  AlertCircle,
  Loader,
} from 'lucide-react'

const Profile = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)
  const [passwordRequested, setPasswordRequested] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [userMetadata, setUserMetadata] = useState({
    name: '',
    phone: '',
  })
  const { userRole, organization } = useOrganization()

  useEffect(() => {
    loadUser()
    // Check for message from navigation state
    if (location.state?.message) {
      setMessage({ type: 'info', text: location.state.message })
    }
  }, [location.state])

  const loadUser = async () => {
    try {
      setLoading(true)
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser()
      
      if (currentUser) {
        setUser(currentUser)
        setUserMetadata({
          name: currentUser.user_metadata?.name || '',
          phone: currentUser.user_metadata?.phone || '',
        })
      }
    } catch (error) {
      console.error('Error loading user:', error)
      setMessage({ type: 'error', text: t('profile.failedToLoad') })
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateProfile = async (e) => {
    e.preventDefault()
    setUpdating(true)
    setMessage({ type: '', text: '' })

    try {
      const { error } = await supabase.auth.updateUser({
        data: {
          name: userMetadata.name,
          phone: userMetadata.phone,
        },
      })

      if (error) throw error

      setMessage({ type: 'success', text: t('profile.profileUpdated') })
      
      // Reload user to get updated metadata
      await loadUser()
    } catch (error) {
      console.error('Error updating profile:', error)
        setMessage({
          type: 'error',
          text: error.message || t('profile.updateFailed'),
        })
    } finally {
      setUpdating(false)
    }
  }

  const handleRequestPasswordChange = async () => {
    if (!user?.email) {
      setMessage({ type: 'error', text: t('profile.emailNotFound') })
      return
    }

    setUpdating(true)
    setMessage({ type: '', text: '' })

    try {
      // Request password reset email
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })

      if (error) throw error

      setPasswordRequested(true)
      setMessage({
        type: 'success',
        text: t('profile.passwordResetSent'),
      })
    } catch (error) {
      console.error('Error requesting password reset:', error)
        setMessage({
          type: 'error',
          text: error.message || t('profile.updateFailed'),
        })
    } finally {
      setUpdating(false)
    }
  }

  if (loading) {
    return (
      <div className="section-container">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader className="animate-spin text-primary-600 dark:text-primary-400" size={32} />
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="section-container">
        <Card variant="soft" className="p-6">
          <div className="flex items-center gap-3 text-red-600">
            <AlertCircle size={24} />
            <p>{t('profile.userNotFound')}</p>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="section-container">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('profile.title')}</h1>
        <p className="text-gray-600 dark:text-gray-400">{t('profile.subtitle')}</p>
      </motion.div>

      {/* Message Alert */}
      {message.text && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Card
            variant="soft"
            className={`p-4 ${
              message.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700'
                : message.type === 'info'
                ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700'
                : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700'
            }`}
          >
            <div className="flex items-center gap-3">
              {message.type === 'success' ? (
                <CheckCircle size={20} className="text-green-600 dark:text-green-400" />
              ) : message.type === 'info' ? (
                <AlertCircle size={20} className="text-blue-600 dark:text-blue-400" />
              ) : (
                <AlertCircle size={20} className="text-red-600 dark:text-red-400" />
              )}
              <p
                className={
                  message.type === 'success'
                    ? 'text-green-800 dark:text-green-200'
                    : message.type === 'info'
                    ? 'text-blue-800 dark:text-blue-200'
                    : 'text-red-800 dark:text-red-200'
                }
              >
                {message.text}
              </p>
            </div>
          </Card>
        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Information */}
        <div className="lg:col-span-2">
          <Card variant="soft" className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center">
                <User size={24} className="text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('profile.profileInformation')}</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('profile.updatePersonalDetails')}</p>
              </div>
            </div>

            <form onSubmit={handleUpdateProfile} className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('profile.fullName')}
                </label>
                <input
                  type="text"
                  value={userMetadata.name}
                  onChange={(e) =>
                    setUserMetadata({ ...userMetadata, name: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  placeholder={t('profile.fullNamePlaceholder')}
                />
              </div>

              {/* Email (Read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('profile.emailAddress')}
                </label>
                <div className="relative">
                  <Mail
                    size={20}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500"
                  />
                  <input
                    type="email"
                    value={user.email || ''}
                    disabled
                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('profile.emailCannotChange')}
                </p>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('profile.phoneNumber')}
                </label>
                <input
                  type="tel"
                  value={userMetadata.phone}
                  onChange={(e) =>
                    setUserMetadata({ ...userMetadata, phone: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  placeholder={t('profile.phonePlaceholder')}
                />
              </div>

              {/* Submit Button */}
              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={updating}
                  className="flex items-center gap-2"
                >
                  {updating ? (
                    <>
                      <Loader className="animate-spin" size={18} />
                      {t('profile.updating')}
                    </>
                  ) : (
                    t('profile.saveChanges')
                  )}
                </Button>
              </div>
            </form>
          </Card>
        </div>

        {/* Account Details & Security */}
        <div className="space-y-6">
          {/* Account Details */}
          <Card variant="soft" className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Shield size={20} className="text-primary-600 dark:text-primary-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('profile.accountDetails')}</h3>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('profile.role')}</p>
                <p className="text-base font-semibold text-gray-900 dark:text-gray-100 capitalize">
                  {userRole || 'Member'}
                </p>
              </div>
              {organization && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t('profile.organization')}</p>
                  <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    {organization.name}
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('profile.accountCreated')}</p>
                <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  {new Date(user.created_at).toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('profile.lastSignIn')}</p>
                <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  {user.last_sign_in_at
                    ? new Date(user.last_sign_in_at).toLocaleDateString()
                    : t('profile.never')}
                </p>
              </div>
            </div>
          </Card>

          {/* Password Security */}
          <Card variant="soft" className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Key size={20} className="text-primary-600 dark:text-primary-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('profile.passwordSecurity')}</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t('profile.passwordResetDescription')}
            </p>
            {passwordRequested ? (
              <div className="p-4 rounded-xl bg-green-50 border border-green-200">
                <div className="flex items-center gap-2 text-green-800">
                  <CheckCircle size={18} />
                  <p className="text-sm font-medium">
                    {t('profile.passwordResetSent')}
                  </p>
                </div>
                <p className="text-xs text-green-700 mt-2">
                  {t('profile.passwordResetInstructions')}
                </p>
              </div>
            ) : (
              <Button
                variant="secondary"
                onClick={handleRequestPasswordChange}
                disabled={updating}
                className="w-full flex items-center justify-center gap-2"
              >
                {updating ? (
                  <>
                    <Loader className="animate-spin" size={18} />
                    {t('profile.sending')}
                  </>
                ) : (
                  <>
                    <Key size={18} />
                    {t('profile.requestPasswordChange')}
                  </>
                )}
              </Button>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}

export default Profile

